import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ChevronDown, ChevronRight, ChevronUp, Loader2, Plus, Search, Trash2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { erpAPI, adminAPI } from '../../services/api';
import Toast from '../../components/admin/Toast';
import SearchableSelect from '../../components/admin/SearchableSelect';
import { rows, errorText, money } from '../../utils/erpHelpers';
import './ERPManagement.css';
import './PurchaseManagement.css';

type Purpose = 'REPLENISHMENT' | 'SHOP_RETURN' | 'SALE_RETURN' | 'REBALANCING' | 'OTHER';

const PURPOSES: { value: Purpose; label: string }[] = [
  { value: 'REPLENISHMENT', label: 'Warehouse replenishment' },
  { value: 'SHOP_RETURN', label: 'Shop stock return' },
  { value: 'SALE_RETURN', label: 'Customer sale return to warehouse' },
  { value: 'REBALANCING', label: 'Location rebalancing' },
  { value: 'OTHER', label: 'Other documented transfer' },
];

type TransferRow = {
  key: string;
  product_variant: number;
  product: number;
  product_name: string;
  product_code: string;
  sku: string;
  color: string;
  size: string;
  available: number;
  avg_cost: number;
  quantity: number;
};

type SearchEntry =
  | { _type: 'product'; id: number; name_en: string; name_bn: string; sku: string }
  | {
      _type: 'variant'; id: number; product: number; product_name: string; product_code: string;
      sku: string; color: string; size: string; available: number; avg_cost: number | null;
    };

const variantLabel = (r: TransferRow) => `${r.product_name}${r.color || r.size ? ` — ${[r.color, r.size].filter(Boolean).join(' / ')}` : ''} (${r.sku})`;

const StockTransferCreate: React.FC = () => {
  const navigate = useNavigate();
  const [toast, setToast] = useState<{ type: 'success' | 'error' | 'warning'; text: string } | null>(null);

  const [locations, setLocations] = useState<any[]>([]);
  const [brands, setBrands] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [ready, setReady] = useState(false);

  const [purpose, setPurpose] = useState<string>('REPLENISHMENT');
  const [source, setSource] = useState<string>(() => sessionStorage.getItem('erp-location-filter') || '');
  const [destination, setDestination] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const [rows_, setRows_] = useState<TransferRow[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Set<string>>(new Set());
  const [collapsedProducts, setCollapsedProducts] = useState<Set<number>>(new Set());
  const [allCollapsed, setAllCollapsed] = useState(false);

  // ── Product / variant search ──
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchEntry[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [highlightedIdx, setHighlightedIdx] = useState(-1);
  const [searchLoading, setSearchLoading] = useState(false);
  const [category, setCategory] = useState('');
  const [brand, setBrand] = useState('');
  const searchRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchDebounce = useRef<any>(null);
  const [localProducts, setLocalProducts] = useState<any[]>([]);

  // ── Source stock map ──
  const [sourceStockMap, setSourceStockMap] = useState<Record<string, { available: number; avg: number }>>({});
  const [loadingStock, setLoadingStock] = useState(false);

  // ── Bulk qty ──
  const [bulkMode, setBulkMode] = useState<'set' | 'inc' | 'dec'>('set');
  const [bulkValue, setBulkValue] = useState('');

  useEffect(() => {
    Promise.all([
      erpAPI.list('locations', {}).then(r => setLocations(rows(r.data))),
      erpAPI.list('brand-options', {}).then(r => setBrands(rows(r.data))).catch(() => {}),
      adminAPI.getCategories().then(r => setCategories(rows(r.data))).catch(() => {}),
      erpAPI.list('products', { page_size: 200 }).then(r => setLocalProducts(rows(r.data))).catch(() => {}),
    ]).catch(() => {}).finally(() => setReady(true));
  }, []);

  const reloadStockMap = useCallback((locId: string) => {
    if (!locId) { setSourceStockMap({}); return; }
    setLoadingStock(true);
    erpAPI.list('inventory', { location: locId, page_size: 500 })
      .then(r => {
        const map: Record<string, { available: number; avg: number }> = {};
        rows(r.data).forEach((s: any) => {
          map[s.product_variant] = { available: Number(s.available_quantity ?? s.quantity ?? 0), avg: Number(s.average_cost || 0) };
        });
        setSourceStockMap(map);
      }).catch(() => {}).finally(() => setLoadingStock(false));
  }, []);

  useEffect(() => { reloadStockMap(source); }, [source, reloadStockMap]);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setShowSearchResults(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    const change = (event: Event) => {
      const location = String((event as CustomEvent).detail?.location || '');
      sessionStorage.setItem('erp-location-filter', location);
      if (location && !source) setSource(location);
    };
    window.addEventListener('erp-location-change', change);
    return () => window.removeEventListener('erp-location-change', change);
  }, [source]);

  // Mirror source stock onto existing rows (availability/cost change with the source).
  useEffect(() => {
    if (!rows_.length) return;
    setRows_(current => current.map(row => {
      const stock = sourceStockMap[row.key];
      return stock ? { ...row, available: stock.available, avg_cost: stock.avg } : { ...row, available: 0 };
    }));
  }, [sourceStockMap, rows_.length]);

  const warehouses = useMemo(() => locations.filter(x => x.location_type === 'WAREHOUSE'), [locations]);
  const shops = useMemo(() => locations.filter(x => x.location_type === 'SHOP'), [locations]);

  const sourceOptions = useMemo(() => {
    if (purpose === 'REPLENISHMENT') return warehouses;
    if (purpose === 'SHOP_RETURN' || purpose === 'SALE_RETURN') return shops;
    return locations;
  }, [purpose, warehouses, shops, locations]);

  const destinationOptions = useMemo(() => {
    const pool = purpose === 'REPLENISHMENT' ? shops : (purpose === 'SHOP_RETURN' || purpose === 'SALE_RETURN') ? warehouses : locations;
    return pool.filter(x => String(x.id) !== String(source));
  }, [purpose, source, warehouses, shops, locations]);

  useEffect(() => {
    // Route rule changed — clear an invalid source/destination.
    const srcOkay = source && sourceOptions.some(x => String(x.id) === source);
    if (source && !srcOkay) setSource('');
  }, [purpose, sourceOptions, source]);

  useEffect(() => {
    const destOkay = destination && destinationOptions.some(x => String(x.id) === destination);
    if (destination && !destOkay) setDestination('');
  }, [destination, destinationOptions]);

  const localProductEntries = useMemo(() => {
    const text = query.toLowerCase().trim();
    if (!text) return [];
    return localProducts.filter(p =>
      p.name_en?.toLowerCase().includes(text) || p.name_bn?.toLowerCase().includes(text) || p.sku?.toLowerCase().includes(text)
    ).slice(0, 5).map(p => ({ _type: 'product' as const, id: p.id, name_en: p.name_en, name_bn: p.name_bn, sku: p.sku }));
  }, [query, localProducts]);

  const doVariantSearch = useCallback(async (q: string) => {
    if (q.trim().length < 1 && !category && !brand) return [];
    try {
      const res = await erpAPI.searchForTransfer({
        q: q.trim() || undefined,
        source: source || undefined,
        category: category || undefined,
        brand: brand || undefined,
      });
      return (Array.isArray(res.data) ? res.data : []).slice(0, 12).map((v: any) => ({
        _type: 'variant' as const,
        id: v.id, product: v.product, product_name: v.product_name,
        product_code: v.product_code, sku: v.sku, color: v.color || '', size: v.size || '',
        available: Number(v.available_quantity ?? 0),
        avg_cost: v.average_cost != null ? Number(v.average_cost) : Number(v.unit_cost || 0),
      }));
    } catch { return []; }
  }, [source, category, brand]);

  const onQueryChange = (value: string) => {
    setQuery(value);
    setHighlightedIdx(-1);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(async () => {
      setSearchLoading(true);
      const variants = await doVariantSearch(value);
      const combined: SearchEntry[] = [...localProductEntries, ...variants];
      setSearchResults(combined);
      setShowSearchResults(combined.length > 0 && (!source || Boolean(source)));
      setSearchLoading(false);
    }, 250);
  };

  // Refresh results when filters change.
  useEffect(() => {
    const t = setTimeout(() => { onQueryChange(query); }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, brand, source]);

  const mergeRow = (prev: TransferRow[], row: TransferRow) => {
    const existing = prev.find(r => r.key === row.key);
    if (existing) {
      const q = Math.min(existing.quantity + 1, Math.max(1, existing.available));
      setToast({ type: 'success', text: `+1 ${row.sku || row.product_name} — qty now ${q}` });
      return prev.map(r => r.key === row.key ? { ...r, quantity: q } : r);
    }
    setToast({ type: 'success', text: `Added ${row.sku || row.product_name}` });
    return [...prev, row];
  };

  const addVariantsFromProduct = async (productId: number, productName: string) => {
    if (!source) { setToast({ type: 'warning', text: 'Choose a source location first.' }); return; }
    try {
      const res = await erpAPI.expandForTransfer({ product: productId, source });
      const list: any[] = Array.isArray(res.data) ? res.data : [];
      let added = 0;
      setRows_(prev => {
        let next = [...prev];
        for (const v of list) {
          const stock = sourceStockMap[v.id] || { available: Number(v.available_quantity ?? 0), avg: Number(v.average_cost || v.unit_cost || 0) };
          const row: TransferRow = {
            key: String(v.id), product_variant: v.id, product: v.product,
            product_name: v.product_name, product_code: v.product_code, sku: v.sku,
            color: v.color || '', size: v.size || '',
            available: stock.available, avg_cost: stock.avg, quantity: 1,
          };
          const exists = next.some(r => r.key === row.key);
          if (exists) {
            next = next.map(r => r.key === row.key ? { ...r, quantity: Math.min(r.quantity + 1, Math.max(1, r.available)) } : r);
          } else {
            next = [...next, row];
          }
          added++;
        }
        return next;
      });
      setToast({ type: 'success', text: added ? `Added ${added} variant${added > 1 ? 's' : ''} from ${productName}` : 'All variants already in the transfer.' });
    } catch (e) { setToast({ type: 'error', text: errorText(e) }); }
  };

  const addSingleVariant = (entry: SearchEntry & { _type: 'variant' }) => {
    if (!source) { setToast({ type: 'warning', text: 'Choose a source location first.' }); return; }
    const stock = sourceStockMap[String(entry.id)] || { available: entry.available, avg: entry.avg_cost || 0 };
    setRows_(prev => mergeRow(prev, {
      key: String(entry.id), product_variant: entry.id, product: entry.product,
      product_name: entry.product_name, product_code: entry.product_code, sku: entry.sku,
      color: entry.color, size: entry.size,
      available: stock.available, avg_cost: stock.avg, quantity: 1,
    }));
  };

  const onSelectEntry = async (entry: SearchEntry) => {
    if (entry._type === 'product') {
      await addVariantsFromProduct(entry.id, entry.name_en);
    } else {
      addSingleVariant(entry);
    }
    setHighlightedIdx(-1);
    setQuery('');
    setTimeout(() => searchInputRef.current?.focus(), 50);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSearchResults || !searchResults.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightedIdx(p => (p < searchResults.length - 1 ? p + 1 : 0)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightedIdx(p => (p > 0 ? p - 1 : searchResults.length - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (highlightedIdx >= 0) onSelectEntry(searchResults[highlightedIdx]); else if (searchResults.length) onSelectEntry(searchResults[0]); }
    else if (e.key === 'Escape') { e.preventDefault(); setShowSearchResults(false); setHighlightedIdx(-1); }
  };

  // ── Row editing ──
  const updateQty = (key: string, value: string) => {
    const q = Math.max(1, Math.round(Number(value) || 0));
    setRows_(current => current.map(row => row.key === key ? { ...row, quantity: q } : row));
  };

  const deleteRow = (key: string) => {
    setRows_(current => current.filter(x => x.key !== key));
    setSelectedRowKeys(prev => { const n = new Set(prev); n.delete(key); return n; });
  };

  const deleteSelectedRows = () => {
    const count = selectedRowKeys.size;
    if (!count) return;
    setRows_(current => current.filter(x => !selectedRowKeys.has(x.key)));
    setSelectedRowKeys(new Set());
    setToast({ type: 'success', text: `Deleted ${count} row${count > 1 ? 's' : ''}.` });
  };

  const toggleRow = (key: string) => setSelectedRowKeys(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const allSelectionState = (): 'all' | 'some' | 'none' => {
    if (!rows_.length) return 'none';
    const selected = rows_.filter(r => selectedRowKeys.has(r.key)).length;
    if (selected === rows_.length) return 'all';
    if (selected > 0) return 'some';
    return 'none';
  };

  const toggleAll = () => {
    setSelectedRowKeys(prev => prev.size === rows_.length ? new Set() : new Set(rows_.map(r => r.key)));
  };

  const productSelectionState = (group: TransferRow[]): 'all' | 'some' | 'none' => {
    if (!group.length) return 'none';
    const selected = group.filter(r => selectedRowKeys.has(r.key)).length;
    if (selected === group.length) return 'all';
    if (selected > 0) return 'some';
    return 'none';
  };

  const toggleProduct = (group: TransferRow[]) => {
    setSelectedRowKeys(prev => {
      const next = new Set(prev);
      const allSelected = group.every(r => next.has(r.key));
      (allSelected ? group : group.filter(r => !next.has(r.key))).forEach(r => allSelected ? next.delete(r.key) : next.add(r.key));
      return next;
    });
  };

  const toggleAllGroups = () => {
    if (allCollapsed) { setAllCollapsed(false); setCollapsedProducts(new Set()); }
    else { setAllCollapsed(true); }
  };

  const applyBulkQty = () => {
    const value = Math.round(Number(bulkValue));
    if (!value) { setToast({ type: 'warning', text: 'Enter a quantity value.' }); return; }
    const keys = selectedRowKeys.size ? selectedRowKeys : new Set(rows_.map(r => r.key));
    setRows_(current => current.map(row => {
      if (!keys.has(row.key)) return row;
      let q = row.quantity;
      if (bulkMode === 'set') q = value;
      else if (bulkMode === 'inc') q += value;
      else q -= value;
      q = Math.max(1, q);
      if (row.available > 0) q = Math.min(q, row.available);
      return { ...row, quantity: q };
    }));
    setToast({ type: 'success', text: `Bulk quantity applied to ${keys.size} row${keys.size !== 1 ? 's' : ''}.` });
  };

  const groupedRows = useMemo(() => {
    const groups: { productName: string; rows: TransferRow[] }[] = [];
    for (const productName of [...new Set(rows_.map(r => r.product_name))]) {
      groups.push({ productName, rows: rows_.filter(r => r.product_name === productName) });
    }
    return groups;
  }, [rows_]);

  const activeRows = useMemo(() => rows_.filter(r => r.quantity > 0), [rows_]);
  const totalUnits = activeRows.reduce((s, r) => s + r.quantity, 0);
  const totalValue = activeRows.reduce((s, r) => s + r.quantity * r.avg_cost, 0);
  const totalVariants = activeRows.length;

  const createTransfer = async () => {
    if (!purpose || !source || !destination) { setToast({ type: 'warning', text: 'Select purpose, source, and destination.' }); return; }
    if (String(source) === String(destination)) { setToast({ type: 'warning', text: 'Source and destination must be different.' }); return; }
    if (purpose === 'OTHER' && !notes.trim()) { setToast({ type: 'warning', text: 'Explain the reason for an Other transfer.' }); return; }
    if (!activeRows.length) { setToast({ type: 'warning', text: 'Add at least one variant to transfer.' }); return; }
    const over = activeRows.find(r => r.available < r.quantity);
    if (over) {
      setToast({ type: 'warning', text: `${variantLabel(over)} has only ${over.available} available at source; you entered ${over.quantity}.` });
      return;
    }
    setSaving(true);
    try {
      await erpAPI.create('transfers', {
        purpose, source: +source, destination: +destination, notes: notes.trim(),
        items: activeRows.map(r => ({ product_variant: r.product_variant, quantity: r.quantity })),
      });
      setToast({ type: 'success', text: 'Transfer created and ready for dispatch.' });
      setTimeout(() => navigate('/admin/inventory/transfers'), 900);
    } catch (e) { setToast({ type: 'error', text: errorText(e) }); }
    finally { setSaving(false); }
  };

  if (!ready) return <div className="erp-loading"><Loader2 size={20} className="spin" /> Loading transfer workspace…</div>;

  return (
    <div className="pm-page">
      {toast && <Toast show message={toast.text} type={toast.type} onClose={() => setToast(null)} />}

      <div className="pm-head">
        <button className="pm-back" onClick={() => navigate('/admin/inventory/transfers')}><ArrowLeft size={18} /></button>
        <div>
          <h1>New Stock Transfer</h1>
          <p className="pm-subtitle">
            Choose the route, search variants, set quantities, then create. Source stock is locked again at dispatch.
          </p>
        </div>
        {totalVariants > 0 && (
          <div className="pm-head-actions">
            <span className="pm-count">{totalVariants} variant{totalVariants > 1 ? 's' : ''}</span>
          </div>
        )}
      </div>

      {/* ══ Transfer route header ══ */}
      <div className="pm-entry-bar">
        <div className="pm-entry-field">
          <label>Transfer purpose *</label>
          <SearchableSelect value={purpose} onChange={next => setPurpose(next)}
            placeholder="Select purpose" options={PURPOSES.map(p => ({ value: p.value, label: p.label }))} />
        </div>
        <div className="pm-entry-field">
          <label>Source location *</label>
          <SearchableSelect value={source} onChange={n => { setSource(n); setDestination(''); }} placeholder="Select source"
            options={sourceOptions.map(x => ({ value: x.id, label: `${x.name} (${x.location_type})` }))} />
        </div>
        <div className="pm-entry-field">
          <label>Destination location *</label>
          <SearchableSelect value={destination} onChange={setDestination} placeholder="Select destination"
            options={destinationOptions.map(x => ({ value: x.id, label: `${x.name} (${x.location_type})` }))} />
        </div>
        <div className="pm-entry-field" style={{ flex: 2 }}>
          <label>Notes</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={1}
            placeholder={purpose === 'OTHER' ? 'Reason is required for Other transfers…' : 'Reference, authorization, handling instructions…'} />
        </div>
      </div>

      {/* ══ Product / variant picker ══ */}
      <div className="pm-product-bar">
        <div className="pm-product-search" ref={searchRef} style={{ flex: 2 }}>
          <Search size={16} style={{ color: '#94a3b8' }} />
          <input ref={searchInputRef} type="text" value={query}
            onChange={e => onQueryChange(e.target.value)}
            onFocus={() => { if (searchResults.length) setShowSearchResults(true); }}
            onKeyDown={handleKeyDown}
            placeholder="Search by barcode, SKU, product name, color, size…" autoComplete="off" />
          {(searchLoading || loadingStock) && <Loader2 size={16} className="spin" />}
          {showSearchResults && searchResults.length > 0 && (
            <div className="pm-product-results">
              {(() => {
                const productEntries = searchResults.filter(e => e._type === 'product');
                const variantEntries = searchResults.filter(e => e._type === 'variant');
                let globalIdx = 0;
                const sections: { label: string; items: SearchEntry[]; startIdx: number }[] = [];
                if (productEntries.length) { sections.push({ label: 'Products (all variants)', items: productEntries, startIdx: globalIdx }); globalIdx += productEntries.length; }
                if (variantEntries.length) { sections.push({ label: 'Variants', items: variantEntries, startIdx: globalIdx }); globalIdx += variantEntries.length; }
                return sections.map(section => (
                  <div key={section.label}>
                    <div className="pm-search-section-label">{section.label}</div>
                    {section.items.map((entry, localIdx) => {
                      const idx = section.startIdx + localIdx;
                      if (entry._type === 'product') {
                        return <button key={`p-${entry.id}`} type="button"
                          className={`pm-product-result ${highlightedIdx === idx ? 'pm-option-hl' : ''}`}
                          onClick={() => onSelectEntry(entry)} onMouseEnter={() => setHighlightedIdx(idx)}>
                          <strong>{entry.name_en}</strong>
                          <span className="pm-vr-detail">{entry.sku} — click to add all variants</span>
                        </button>;
                      }
                      return <button key={`v-${entry.id}`} type="button"
                        className={`pm-variant-result ${highlightedIdx === idx ? 'pm-option-hl' : ''}`}
                        onClick={() => onSelectEntry(entry)} onMouseEnter={() => setHighlightedIdx(idx)}>
                        <span className="pm-vr-product">{entry.product_name}</span>
                        <span className="pm-vr-detail">{entry.color}{entry.color && entry.size ? ' • ' : ''}{entry.size} — {entry.sku}{source ? ` · available ${entry.available}` : ' · choose source first'}</span>
                      </button>;
                    })}
                  </div>
                ));
              })()}
            </div>
          )}
          {showSearchResults && !searchResults.length && !searchLoading && query.trim().length >= 1 && (
            <div className="pm-product-results"><div className="pm-empty">No matching products or variants found.</div></div>
          )}
        </div>
        <div className="pm-filter-control">
          <SearchableSelect value={category} onChange={n => { setCategory(String(n || '')); }} placeholder="All categories"
            options={categories.map((c: any) => ({ value: c.id, label: c.name_en || c.name }))} />
        </div>
        <div className="pm-filter-control">
          <SearchableSelect value={brand} onChange={n => { setBrand(String(n || '')); }} placeholder="All brands"
            options={brands.map((b: any) => ({ value: b.id, label: b.name }))} />
        </div>
        {!source && <span className="pm-hint" style={{ fontSize: 12.5, color: '#b45309' }}>Select a source location to search stock.</span>}
      </div>

      {/* ══ Bulk operations ══ */}
      {rows_.length > 0 && (
        <div className="pm-inline-bulk" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 4px' }}>
          <span style={{ fontSize: 12.5, color: '#64748b' }}>Bulk quantity:</span>
          <select value={bulkMode} onChange={e => setBulkMode(e.target.value as any)} style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}>
            <option value="set">Set</option>
            <option value="inc">Increase by</option>
            <option value="dec">Decrease by</option>
          </select>
          <input type="number" min="1" step="1" value={bulkValue} onChange={e => setBulkValue(e.target.value)}
            placeholder="qty" style={{ width: 90, padding: '7px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }} />
          <button className="pm-btn pm-btn-secondary pm-btn-sm" onClick={applyBulkQty}>Apply</button>
          {selectedRowKeys.size > 0 && (
            <button className="pm-btn pm-btn-danger pm-btn-sm" onClick={deleteSelectedRows}><Trash2 size={13} /> Remove selected ({selectedRowKeys.size})</button>
          )}
          <span className="pm-count" style={{ marginLeft: 'auto' }}>{selectedRowKeys.size ? `${selectedRowKeys.size} selected` : 'Applies to all rows'}</span>
        </div>
      )}

      {/* ══ Transfer items table ══ */}
      <section className="erp-card pm-card-table">
        <div className="erp-table-title">
          <h2 style={{ margin: 0 }}>Transfer Items</h2>
          <span style={{ fontSize: 12.5, color: '#64748b' }}>Source stock is read from {source ? locations.find(x => String(x.id) === source)?.name || 'the source' : 'the source'} location.</span>
        </div>
        {rows_.length === 0 ? (
          <div className="pm-empty pm-empty-box">
            <p>No items yet. Search and select a product or variant above to add transfer lines.</p>
          </div>
        ) : (
          <div className="erp-table-wrap pm-table-wrap">
            <table>
              <colgroup>
                <col style={{ width: 44 }} />
                <col style={{ width: 180 }} />
                <col style={{ width: 120 }} />
                <col style={{ width: 110 }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 110 }} />
                <col style={{ width: 40 }} />
              </colgroup>
              <thead>
                <tr className="pm-header-toggle" onClick={toggleAllGroups}>
                  <th style={{ width: 44 }} onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={allSelectionState() === 'all'}
                      ref={el => { if (el) el.indeterminate = allSelectionState() === 'some'; }}
                      onChange={toggleAll} title={allSelectionState() === 'all' ? 'Deselect all' : 'Select all'} />
                  </th>
                  <th>Product</th><th>Variant</th><th>Source Stock (Avail.)</th>
                  <th className="pm-th-num">Qty</th><th className="pm-th-num">Unit Cost</th><th className="pm-th-num">Line Value</th><th style={{ width: 40 }} />
                </tr>
              </thead>
              <tbody>
                {groupedRows.map(group => {
                  const pid = group.rows[0]?.product;
                  const collapsed = allCollapsed || collapsedProducts.has(pid);
                  return (
                    <React.Fragment key={group.productName}>
                      <tr className="pm-group-row" onClick={() => {
                        setAllCollapsed(false);
                        setCollapsedProducts(prev => {
                          const next = new Set(prev);
                          if (collapsed) next.delete(pid); else next.add(pid);
                          return next;
                        });
                      }}>
                        <td onClick={e => e.stopPropagation()}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <input type="checkbox"
                              checked={productSelectionState(group.rows) === 'all'}
                              ref={el => { if (el) el.indeterminate = productSelectionState(group.rows) === 'some'; }}
                              onChange={() => toggleProduct(group.rows)} />
                            <button className="pm-group-toggle" type="button">{collapsed ? <ChevronRight size={13} /> : <ChevronDown size={13} />}</button>
                          </div>
                        </td>
                        <td colSpan={7}><strong>{group.productName}</strong> <span className="pm-count">{group.rows.length} variant{group.rows.length > 1 ? 's' : ''}</span></td>
                      </tr>
                      {!collapsed && group.rows.map(row => (
                        <tr key={row.key} className={selectedRowKeys.has(row.key) ? 'pm-row-selected' : ''}>
                          <td><input type="checkbox" checked={selectedRowKeys.has(row.key)} onChange={() => toggleRow(row.key)} /></td>
                          <td className="pm-prod-name" style={{ fontWeight: 500 }}>{row.product_name}</td>
                          <td>{[row.color, row.size].filter(Boolean).join(' / ') || <code>{row.sku}</code>}</td>
                          <td className="pm-td-num">
                            <span style={{ color: row.available < row.quantity ? '#dc2626' : '#475569', fontWeight: 600 }}>{row.available}</span>
                            {row.available < row.quantity && <small style={{ color: '#dc2626', display: 'block' }}>exceeds available</small>}
                          </td>
                          <td className="pm-td-num"><input type="number" min="1" step="1" value={row.quantity}
                            onChange={e => updateQty(row.key, e.target.value)} className="pm-qty-input" /></td>
                          <td className="pm-td-num">{money(row.avg_cost)}</td>
                          <td className="pm-td-num pm-line-total">{money(row.quantity * row.avg_cost)}</td>
                          <td><button className="pm-row-del" onClick={() => deleteRow(row.key)}><Trash2 size={13} /></button></td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ══ Sticky summary footer ══ */}
      {rows_.length > 0 && (
        <div className="pm-footer-bar">
          <div className="pm-footer-left">
            <div className="pm-field-inline"><label>Total value</label><span style={{ fontWeight: 700, fontSize: 14 }}>{money(totalValue)}</span></div>
            <div className="pm-field-inline"><label>Units</label><span style={{ fontWeight: 700 }}>{totalUnits}</span></div>
            <div className="pm-field-inline"><label>Route</label><span style={{ fontSize: 12.5, color: '#475569' }}>
              {source ? locations.find(x => String(x.id) === source)?.name : '—'} → {destination ? locations.find(x => String(x.id) === destination)?.name : '—'}
            </span></div>
          </div>
          <div className="pm-footer-right">
            <div className="pm-total-row">
              <span>{totalVariants} variant{totalVariants > 1 ? 's' : ''} · {totalUnits} units · <strong style={{ color: '#334155' }}>{money(totalValue)}</strong></span>
              <button className="pm-btn pm-btn-primary pm-btn-post" disabled={saving || !source || !destination || !activeRows.length} onClick={createTransfer}>
                {saving ? <><Loader2 size={14} className="spin" /> Creating…</> : 'Create Transfer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockTransferCreate;